<template>
  <b-modal
    v-model="show"
    size="lg"
    :title="pedido ? `Factura proforma — ${nombreCliente}` : 'Factura proforma'"
    hide-footer
    centered
  >
    <div v-if="pedido" class="pf-contenido">
      <!-- Overlay mientras se genera el documento y su PDF -->
      <div v-if="generando" class="pf-overlay">
        <b-spinner style="width: 2.2rem; height: 2.2rem;" />
        <span class="pf-overlay__texto">Generando documento y PDF…</span>
      </div>

      <div v-if="cargandoInfo" class="text-center my-3">
        <b-spinner small class="align-middle mr-50" /> Cargando información…
      </div>

      <template v-else>
      <!-- Proformas ya generadas -->
      <div v-if="proformas.length" class="pf-block mb-2">
        <div class="pf-block__title pf-block__title--flex">
          <span><i class="fa-solid fa-file-invoice mr-50"></i>Proformas generadas</span>
          <span v-if="importeCobrado > 0 || importePorCobrar > 0">
            <b-badge v-if="importeCobrado > 0" variant="light-success">{{ pedido.simbolo }}{{ importeCobrado.toFixed(2) }} cobrado</b-badge>
            <b-badge v-if="importePorCobrar > 0" variant="light-warning" class="ml-25">{{ pedido.simbolo }}{{ importePorCobrar.toFixed(2) }} por cobrar</b-badge>
          </span>
        </div>

        <div v-for="pf in proformas" :key="pf.id" class="pf-group">
          <div class="pf-row">
            <b-button
              size="sm"
              variant="flat-secondary"
              class="btn-icon"
              :title="pfAbierta === pf.id ? 'Ocultar productos' : 'Ver productos'"
              @click="pfAbierta = pfAbierta === pf.id ? null : pf.id"
            >
              <i class="fa-solid" :class="pfAbierta === pf.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </b-button>
            <div class="min-w-0">
              <span class="font-weight-bold">{{ pf.numero }}</span>
              <small class="text-muted ml-50">{{ formatFecha(pf.fecha) }}</small>
              <b-badge v-if="pf.tipo === 'deposito'" variant="light-info" class="ml-50">Depósito {{ Number(pf.proformaPct) }}%</b-badge>
              <b-badge v-else-if="Number(pf.proformaPct) !== 100" variant="light-primary" class="ml-50">{{ Number(pf.proformaPct) }}%</b-badge>
              <small class="text-muted d-block">{{ resumenItems(pf) }}</small>
            </div>

            <!-- Cobro 1: Total amount due -->
            <div class="pf-cobro ml-auto">
              <button
                type="button"
                class="pf-pay"
                :class="{ 'is-paid': pf.pagada }"
                :title="pf.pagada ? 'Marcar como no pagado' : 'Marcar como pagado (se anota en el historial de pagos)'"
                :disabled="pagandoClave === `${pf.id}-due`"
                @click="togglePagada(pf, 'due')"
              >
                <span class="pf-pay__check">
                  <b-spinner v-if="pagandoClave === `${pf.id}-due`" small class="pf-pay__spinner" />
                  <i v-else class="fa-solid fa-check"></i>
                </span>
                <span class="pf-pay__info">
                  <span class="pf-pay__linea"><span class="pf-pay__label">Due</span>{{ pf.simbolo }}{{ fmtImporte(pf.totalDue) }}</span>
                  <span class="pf-pay__estado">{{ pf.pagada ? 'Pagado' : 'Pendiente' }}</span>
                </span>
              </button>
            </div>

            <!-- Cobro 2: Remaining balance (a X días) -->
            <div class="pf-cobro" v-if="Number(pf.restante) > 0">
              <button
                type="button"
                class="pf-pay"
                :class="{ 'is-paid': pf.pagadaRestante, 'is-overdue': !pf.pagadaRestante && restanteVencido(pf) }"
                :title="pf.pagadaRestante ? 'Marcar como no pagado' : 'Marcar como pagado (se anota en el historial de pagos)'"
                :disabled="pagandoClave === `${pf.id}-restante`"
                @click="togglePagada(pf, 'restante')"
              >
                <span class="pf-pay__check">
                  <b-spinner v-if="pagandoClave === `${pf.id}-restante`" small class="pf-pay__spinner" />
                  <i v-else class="fa-solid" :class="!pf.pagadaRestante && restanteVencido(pf) ? 'fa-exclamation' : 'fa-check'"></i>
                </span>
                <span class="pf-pay__info">
                  <span class="pf-pay__linea"><span class="pf-pay__label">Resto</span>{{ pf.simbolo }}{{ fmtImporte(pf.restante) }}</span>
                  <span class="pf-pay__estado">
                    {{ pf.pagadaRestante ? 'Pagado' : (pf.restanteFecha ? ((restanteVencido(pf) ? 'venció ' : 'vence ') + formatFecha(pf.restanteFecha)) : 'Pendiente') }}
                  </span>
                </span>
              </button>
            </div>

            <b-button size="sm" variant="flat-danger" class="btn-icon" title="Ver PDF" @click="verPdf(pf.id)">
              <i class="fa-solid fa-file-pdf"></i>
            </b-button>
          </div>
          <div v-if="pfAbierta === pf.id" class="pf-detalle">
            <div v-for="(it, i) in pf.items" :key="i" class="pf-detalle__row">
              <div class="min-w-0">
                <span class="pf-detalle__title">{{ it.title || it.sku }}</span>
                <small class="text-muted d-block">{{ it.sku }}</small>
              </div>
              <span class="text-muted">x{{ it.qty }}</span>
              <span class="text-muted">{{ pf.simbolo }}{{ Number(it.unitPrice).toFixed(2) }}</span>
              <span class="font-weight-bold">{{ pf.simbolo }}{{ (it.qty * it.unitPrice).toFixed(2) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Facturas (invoice legal) -->
      <div class="pf-block mb-2">
        <div class="pf-block__title"><i class="fa-solid fa-file-invoice-dollar mr-50"></i>Facturas</div>
        <div class="pf-block__body">
          <div v-for="fa in facturas" :key="'fa' + fa.id" class="pf-row pf-row--factura">
            <div class="min-w-0">
              <span class="font-weight-bold">{{ fa.numero }}</span>
              <small class="text-muted ml-50">{{ formatFecha(fa.fecha) }}</small>
              <b-badge variant="light-primary" class="ml-50">
                {{ fa.vatTipo === 'uk20' ? 'VAT 20%' : (fa.vatTipo === 'reverse' ? 'Reverse charge' : 'Export 0%') }}
              </b-badge>
              <small class="text-muted d-block">Total {{ fa.simbolo }}{{ fmtImporte(fa.total) }}</small>
            </div>
            <button
              type="button"
              class="pf-pay ml-auto"
              :class="{ 'is-paid': fa.pagada }"
              :title="fa.pagada ? 'Marcar como no pagada' : 'Marcar como pagada (se anota en el historial de pagos)'"
              :disabled="pagandoFacturaId === fa.id"
              @click="toggleFacturaPagada(fa)"
            >
              <span class="pf-pay__check">
                <b-spinner v-if="pagandoFacturaId === fa.id" small class="pf-pay__spinner" />
                <i v-else class="fa-solid fa-check"></i>
              </span>
              <span class="pf-pay__info">
                <span class="pf-pay__linea"><span class="pf-pay__label">Due</span>{{ fa.simbolo }}{{ fmtImporte(fa.balanceDue) }}</span>
                <span class="pf-pay__estado">{{ fa.pagada ? ('Pagada' + (fa.fechaPago ? ' ' + formatFecha(fa.fechaPago) : '')) : 'Pendiente' }}</span>
              </span>
            </button>
            <b-button size="sm" variant="flat-danger" class="btn-icon" title="Ver PDF" @click="verPdfFactura(fa.id)">
              <i class="fa-solid fa-file-pdf"></i>
            </b-button>
          </div>
          <div v-if="!facturas.length" class="text-muted small">Sin facturas emitidas.</div>
        </div>
      </div>

      <!-- Estado de fabricación B2B -->
      <div v-if="pedidosTaller.length" class="pf-block mb-2">
        <div class="pf-block__title"><i class="fa-solid fa-industry mr-50"></i>Pedidos de taller asociados</div>
        <div class="pf-block__body">
          <b-badge
            v-for="pt in pedidosTaller"
            :key="pt.id"
            variant="light-primary"
            class="mr-50"
          >
            {{ pt.codigo }} — {{ pt.estado }}
          </b-badge>
        </div>
      </div>

      <!-- Pedido facturado al completo: no se permiten más facturas, depósitos ni proformas -->
      <div v-if="pedidoFacturado" class="pf-block mb-2">
        <div class="pf-block__body text-muted small">
          <i class="fa-solid fa-circle-check mr-50 text-success"></i>Pedido facturado al completo.
        </div>
      </div>

      <!-- Botones para abrir los formularios -->
      <div v-if="!pedidoFacturado && !mostrarNueva && !mostrarDeposito && !mostrarFactura" class="pf-acciones">
        <button v-if="!tieneDeposito" class="pf-accion" @click="mostrarDeposito = true">
          <span class="pf-accion__icono"><i class="fa-solid fa-piggy-bank"></i></span>
          <span class="pf-accion__titulo">Crear depósito</span>
          <small class="pf-accion__desc">Anticipo (%) sobre el pedido completo</small>
        </button>
        <button v-if="hayUnidadesDisponibles" class="pf-accion pf-accion--primary" @click="nuevaProforma">
          <span class="pf-accion__icono"><i class="fa-solid fa-file-circle-plus"></i></span>
          <span class="pf-accion__titulo">Nueva proforma</span>
          <small class="pf-accion__desc">Factura los productos pendientes</small>
        </button>
        <button class="pf-accion" @click="abrirFactura">
          <span class="pf-accion__icono"><i class="fa-solid fa-stamp"></i></span>
          <span class="pf-accion__titulo">Emitir factura</span>
          <small class="pf-accion__desc">Invoice legal, numeración correlativa</small>
        </button>
      </div>

      <!-- Nueva factura: productos editables + condiciones + resumen -->
      <div v-if="mostrarFactura" class="pf-block pf-block--nueva">
        <div class="pf-block__title pf-block__title--main pf-block__title--flex">
          <span><i class="fa-solid fa-stamp mr-50"></i>Emitir factura</span>
          <button class="pf-cancelar" @click="mostrarFactura = false">
            <i class="fa-solid fa-xmark mr-25"></i>Cerrar
          </button>
        </div>

        <!-- Productos: se pueden desmarcar y ajustar cantidad/precio -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-box mr-50"></i>Productos</div>
        <div class="pf-items">
          <div class="pf-item pf-item--head">
            <span></span>
            <span>Producto</span>
            <span></span>
            <span class="text-center">Cant.</span>
            <span class="text-right">Precio</span>
          </div>
          <div v-for="(l, i) in lineasFactura" :key="'lf' + i" class="pf-item" :class="{ 'pf-item--off': !l.sel }">
            <b-form-checkbox v-model="l.sel" class="pf-check" />
            <div class="min-w-0">
              <div class="pf-item__title">{{ l.title || l.sku }}</div>
              <small class="text-muted">{{ l.sku }}</small>
            </div>
            <div></div>
            <b-form-input v-model="l.qty" type="number" min="0" size="sm" class="pf-input" :disabled="!l.sel" />
            <b-form-input v-model="l.unitPrice" type="number" min="0" step="0.01" size="sm" class="pf-input" :disabled="!l.sel" />
          </div>
        </div>
        </div>

        <!-- Condiciones -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-sliders mr-50"></i>Condiciones</div>
        <b-row class="p-1">
          <b-col cols="6" md="3">
            <label class="pf-label">Shipping</label>
            <b-form-input v-model="facturaForm.shipping" type="number" min="0" step="0.01" size="sm" />
          </b-col>
          <b-col cols="6" md="5">
            <label class="pf-label">VAT</label>
            <b-form-select v-model="facturaForm.vatTipo" :options="vatOptions" size="sm" />
          </b-col>
          <b-col cols="6" md="4">
            <label class="pf-label">Vencimiento (si queda pendiente)</label>
            <b-form-input v-model="facturaForm.vencimiento" type="date" size="sm" />
          </b-col>
          <b-col cols="12" class="mt-50">
            <label class="pf-label">Notas</label>
            <b-form-textarea v-model="facturaForm.notas" rows="2" size="sm" />
          </b-col>
        </b-row>
        </div>

        <!-- Resumen + emitir -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-calculator mr-50"></i>Resumen</div>
        <div class="pf-block__body pf-footer">
          <div>
            <small class="text-muted d-block">Subtotal</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ facturaSubtotal.toFixed(2) }}</span>
          </div>
          <div v-if="Number(facturaForm.shipping) > 0">
            <small class="text-muted d-block">Shipping</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ Number(facturaForm.shipping).toFixed(2) }}</span>
          </div>
          <div>
            <small class="text-muted d-block">VAT ({{ facturaVatPct }}%)</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ facturaVatImporte.toFixed(2) }}</span>
          </div>
          <div>
            <small class="text-muted d-block">Total</small>
            <span class="font-weight-bolder text-primary">{{ pedido.simbolo }}{{ facturaTotal.toFixed(2) }}</span>
          </div>
          <div v-if="Number(pedido.pagado) > 0">
            <small class="text-muted d-block">Pagos recibidos</small>
            <span class="font-weight-bolder text-danger">-{{ pedido.simbolo }}{{ Number(pedido.pagado).toFixed(2) }}</span>
          </div>
          <div v-if="Number(pedido.pagado) > 0">
            <small class="text-muted d-block">Balance due</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ facturaBalanceDue.toFixed(2) }}</span>
          </div>
          <b-button
            variant="primary"
            class="ml-auto"
            :disabled="emitiendoFactura || !lineasFacturaValidas.length"
            @click="emitirFactura"
          >
            <b-spinner v-if="emitiendoFactura" small class="mr-50" />
            <i v-else class="fa-solid fa-stamp mr-50"></i> Emitir factura
          </b-button>
        </div>
        <small class="text-muted d-block px-1 pb-1">
          Numeración correlativa: una vez emitida no se puede modificar ni borrar.
        </small>
        </div>
      </div>

      <!-- Depósito: sobre el pedido completo, eligiendo el % -->
      <div v-if="mostrarDeposito" class="pf-block pf-block--nueva">
        <div class="pf-block__title pf-block__title--main pf-block__title--flex">
          <span><i class="fa-solid fa-piggy-bank mr-50"></i>Crear depósito — pedido completo</span>
          <button class="pf-cancelar" @click="mostrarDeposito = false">
            <i class="fa-solid fa-xmark mr-25"></i>Cerrar
          </button>
        </div>
        <div class="pf-block__body pf-footer">
          <div>
            <label class="pf-label">% Depósito</label>
            <b-form-input v-model="depositoPct" type="number" min="1" max="100" size="sm" style="width: 90px" />
          </div>
          <div>
            <small class="text-muted d-block">Pedido completo</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ orderValuePedido.toFixed(2) }}</span>
          </div>
          <div>
            <small class="text-muted d-block">Importe del depósito</small>
            <span class="font-weight-bolder text-primary">
              {{ pedido.simbolo }}{{ ((orderValuePedido * (Number(depositoPct) || 0)) / 100).toFixed(2) }}
            </span>
          </div>
          <b-button
            variant="primary"
            class="ml-auto"
            :disabled="generando || !(Number(depositoPct) > 0)"
            @click="generarDeposito"
          >
            <b-spinner v-if="generando" small class="mr-50" />
            <i v-else class="fa-solid fa-piggy-bank mr-50"></i> Generar depósito
          </b-button>
        </div>
      </div>

      <!-- Nueva proforma: productos + condiciones + resumen -->
      <div v-if="mostrarNueva" class="pf-block pf-block--nueva">
        <div class="pf-block__title pf-block__title--main pf-block__title--flex">
          <span><i class="fa-solid fa-file-circle-plus mr-50"></i>Nueva proforma</span>
          <button class="pf-cancelar" @click="cerrarNueva">
            <i class="fa-solid fa-xmark mr-25"></i>Cerrar
          </button>
        </div>

        <!-- Selección de productos -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-box mr-50"></i>Productos</div>
        <div class="pf-items">
          <div class="pf-item pf-item--head">
            <span></span>
            <span>Producto</span>
            <span>Fabricación</span>
            <span class="text-center">Cant.</span>
            <span class="text-right">Precio</span>
          </div>
          <div v-for="(l, i) in lineas" :key="i" class="pf-item" :class="{ 'pf-item--off': !l.sel }">
            <b-form-checkbox v-model="l.sel" class="pf-check" />
            <div class="min-w-0">
              <div class="pf-item__title">{{ l.title || l.sku }}</div>
              <small class="text-muted">{{ l.sku }}</small>
              <small v-if="l.facturado > 0" class="d-block" :class="l.pedidoQty - l.facturado > 0 ? 'text-warning' : 'text-muted'">
                En proformas {{ Math.min(l.facturado, l.pedidoQty) }}/{{ l.pedidoQty }} uds
                <template v-if="l.pedidoQty - l.facturado > 0"> · quedan {{ l.pedidoQty - l.facturado }}</template>
              </small>
            </div>
            <div>
              <small v-if="fabricacion[l.sku.toUpperCase()]" class="pf-fab">
                Corte {{ fabricacion[l.sku.toUpperCase()].corte }}/{{ fabricacion[l.sku.toUpperCase()].total }}
                · Mano {{ fabricacion[l.sku.toUpperCase()].mano }}/{{ fabricacion[l.sku.toUpperCase()].total }}
                · <b>Term. {{ fabricacion[l.sku.toUpperCase()].terminado }}/{{ fabricacion[l.sku.toUpperCase()].total }}</b>
              </small>
              <small v-else class="text-muted">—</small>
            </div>
            <b-form-input v-model="l.qty" type="number" min="0" size="sm" class="pf-input" :disabled="!l.sel" />
            <b-form-input v-model="l.unitPrice" type="number" min="0" step="0.01" size="sm" class="pf-input" :disabled="!l.sel" />
          </div>
        </div>
        </div>

        <!-- Condiciones -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-sliders mr-50"></i>Condiciones</div>
        <b-row class="p-1">
          <b-col cols="6" md="2">
            <label class="pf-label">% Proforma</label>
            <b-form-input v-model="form.proformaPct" type="number" min="0" max="100" size="sm" />
          </b-col>
          <b-col cols="6" md="3">
            <label class="pf-label">Depósito recibido %</label>
            <b-form-input v-model="form.depositPct" type="number" min="0" max="100" size="sm" />
          </b-col>
          <b-col cols="6" md="2">
            <label class="pf-label">Shipping</label>
            <b-form-input v-model="form.shipping" type="number" min="0" step="0.01" size="sm" />
          </b-col>
          <b-col cols="6" md="3">
            <label class="pf-label">Resto pagadero en (días)</label>
            <b-form-input v-model="form.restanteDias" type="number" min="1" size="sm" />
          </b-col>
          <b-col cols="6" md="2">
            <label class="pf-label">Validez (días)</label>
            <b-form-input v-model="form.expiryDays" type="number" min="1" size="sm" />
          </b-col>
          <b-col cols="6" md="3" class="mt-50">
            <label class="pf-label">Incoterms</label>
            <b-form-input v-model="form.incoterms" size="sm" />
          </b-col>
          <b-col cols="6" md="9" class="mt-50">
            <label class="pf-label">Payment terms</label>
            <b-form-input v-model="form.paymentTerms" size="sm" />
          </b-col>
          <b-col cols="12" class="mt-50">
            <b-form-checkbox v-model="form.envioDiferente">
              <span class="pf-label mb-0">Dirección de envío diferente</span>
            </b-form-checkbox>
          </b-col>
          <template v-if="form.envioDiferente">
            <b-col cols="12" md="4" class="mt-50">
              <label class="pf-label">Nombre (envío)</label>
              <b-form-input v-model="form.envioNombre" size="sm" />
            </b-col>
            <b-col cols="12" md="4" class="mt-50">
              <label class="pf-label">Empresa (envío)</label>
              <b-form-input v-model="form.envioEmpresa" size="sm" />
            </b-col>
            <b-col cols="12" md="4" class="mt-50">
              <label class="pf-label">Teléfono (envío)</label>
              <b-form-input v-model="form.envioTelefono" size="sm" placeholder="+34..." />
            </b-col>
            <b-col cols="12" md="6" class="mt-50">
              <label class="pf-label">Dirección (envío)</label>
              <b-form-input v-model="form.envioDireccion" size="sm" />
            </b-col>
            <b-col cols="4" md="2" class="mt-50">
              <label class="pf-label">C.P. (envío)</label>
              <b-form-input v-model="form.envioCp" size="sm" />
            </b-col>
            <b-col cols="8" md="2" class="mt-50">
              <label class="pf-label">Ciudad (envío)</label>
              <b-form-input v-model="form.envioCiudad" size="sm" />
            </b-col>
            <b-col cols="12" md="2" class="mt-50">
              <label class="pf-label">País (envío)</label>
              <b-form-input v-model="form.envioPais" size="sm" />
            </b-col>
          </template>
          <b-col cols="12" class="mt-50">
            <label class="pf-label">Notas</label>
            <b-form-textarea v-model="form.notas" rows="2" size="sm" />
          </b-col>
        </b-row>
        </div>

        <!-- Resumen + generar -->
        <div class="pf-sub">
        <div class="pf-block__title"><i class="fa-solid fa-calculator mr-50"></i>Resumen</div>
        <div class="pf-block__body pf-footer">
          <div>
            <small class="text-muted d-block">Order value</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ orderValue.toFixed(2) }}</span>
          </div>
          <div>
            <small class="text-muted d-block">Total due</small>
            <span class="font-weight-bolder text-primary">{{ pedido.simbolo }}{{ totalDue.toFixed(2) }}</span>
          </div>
          <div v-if="depositoAmt > 0">
            <small class="text-muted d-block">Depósito</small>
            <span class="font-weight-bolder text-danger">-{{ pedido.simbolo }}{{ depositoAmt.toFixed(2) }}</span>
          </div>
          <div v-if="restante > 0">
            <small class="text-muted d-block">Resto (a {{ form.restanteDias }} días)</small>
            <span class="font-weight-bolder">{{ pedido.simbolo }}{{ restante.toFixed(2) }}</span>
          </div>
          <b-button
            variant="primary"
            class="ml-auto"
            :disabled="generando || !lineasValidas.length"
            @click="generar"
          >
            <b-spinner v-if="generando" small class="mr-50" />
            <i v-else class="fa-solid fa-file-invoice mr-50"></i> Generar proforma
          </b-button>
        </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Fecha del pago al marcar como pagado (facturas, proformas y depósitos) -->
    <b-modal
      v-model="fechaPagoVisible"
      title="Fecha de pago"
      size="sm"
      centered
      ok-title="Confirmar"
      cancel-title="Cancelar"
      @ok="confirmarFechaPago"
    >
      <b-form-input v-model="fechaPagoFecha" type="date" />
    </b-modal>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BBadge, BButton, BRow, BCol, BFormInput, BFormTextarea, BFormCheckbox,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BModal, BSpinner, BBadge, BButton, BRow, BCol, BFormInput, BFormTextarea, BFormCheckbox,
  },
  data() {
    return {
      show: false,
      pedido: null,
      lineas: [],
      proformas: [],
      pedidosTaller: [],
      fabricacion: {},
      generando: false,
      cargandoInfo: false,
      pagandoClave: null,
      pfAbierta: null,
      facturas: [],
      mostrarFactura: false,
      lineasFactura: [],
      facturaForm: { shipping: 0, vatTipo: 'export', notas: '', vencimiento: '' },
      emitiendoFactura: false,
      pagandoFacturaId: null,
      fechaPagoVisible: false,
      fechaPagoFecha: '',
      fechaPagoCb: null,
      vatOptions: [
        { value: 'export', text: 'VAT 0% — Export fuera de UK' },
        { value: 'reverse', text: 'VAT 0% — Reverse charge (B2B UE)' },
        { value: 'uk20', text: 'VAT 20% — UK' },
      ],
      mostrarNueva: false,
      mostrarDeposito: false,
      depositoPct: 30,
      form: {},
    }
  },
  computed: {
    nombreCliente() {
      const c = (this.pedido && this.pedido.cliente) || {}
      return c.empresa || [c.nombre, c.apellidos].filter(Boolean).join(' ') || '—'
    },
    lineasValidas() {
      return this.lineas.filter(l => l.sel && Number(l.qty) > 0)
    },
    // Cobros de todas las proformas del pedido: due y restante, pagados o no
    importeCobrado() {
      return Math.round(this.proformas.reduce((acc, p) => acc
        + (p.pagada ? Number(p.totalDue) || 0 : 0)
        + (p.pagadaRestante ? Number(p.restante) || 0 : 0), 0) * 100) / 100
    },
    importePorCobrar() {
      return Math.round(this.proformas.reduce((acc, p) => acc
        + (!p.pagada ? Number(p.totalDue) || 0 : 0)
        + (!p.pagadaRestante ? Number(p.restante) || 0 : 0), 0) * 100) / 100
    },
    orderValue() {
      return this.lineasValidas.reduce((acc, l) => acc + Number(l.qty) * (Number(l.unitPrice) || 0), 0)
    },
    // Valor del pedido completo (para el depósito)
    orderValuePedido() {
      return ((this.pedido && this.pedido.items) || [])
        .reduce((acc, it) => acc + (parseInt(it.qty, 10) || 0) * (Number(it.wspUnit) || 0), 0)
    },
    // Quedan unidades del pedido sin incluir en ninguna proforma
    hayUnidadesDisponibles() {
      const consumido = {}
      this.proformas.filter(pf => pf.tipo !== 'deposito').forEach(pf => {
        (pf.items || []).forEach(it => {
          const k = (it.sku || '').toUpperCase()
          consumido[k] = (consumido[k] || 0) + (parseInt(it.qty, 10) || 0)
        })
      })
      return ((this.pedido && this.pedido.items) || [])
        .some(it => (parseInt(it.qty, 10) || 0) > (consumido[(it.sku || '').toUpperCase()] || 0))
    },
    // ---- Factura: líneas seleccionadas y totales del formulario ----
    lineasFacturaValidas() {
      return this.lineasFactura.filter(l => l.sel && (parseInt(l.qty, 10) || 0) > 0)
    },
    facturaSubtotal() {
      return Math.round(this.lineasFacturaValidas
        .reduce((acc, l) => acc + (parseInt(l.qty, 10) || 0) * (Number(l.unitPrice) || 0), 0) * 100) / 100
    },
    facturaVatPct() {
      return this.facturaForm.vatTipo === 'uk20' ? 20 : 0
    },
    facturaVatImporte() {
      const base = this.facturaSubtotal + (Number(this.facturaForm.shipping) || 0)
      return Math.round(base * this.facturaVatPct) / 100
    },
    facturaTotal() {
      return Math.round((this.facturaSubtotal + (Number(this.facturaForm.shipping) || 0) + this.facturaVatImporte) * 100) / 100
    },
    facturaBalanceDue() {
      return Math.max(0, Math.round((this.facturaTotal - (Number(this.pedido && this.pedido.pagado) || 0)) * 100) / 100)
    },
    // Dirección de envío como texto multilínea para el PDF (vacía si no es diferente)
    shippingAddressStr() {
      if (!this.form.envioDiferente) return ''
      return [
        this.form.envioNombre,
        this.form.envioEmpresa,
        this.form.envioDireccion,
        [this.form.envioCp, this.form.envioCiudad, this.form.envioPais].filter(Boolean).join(' '),
        this.form.envioTelefono ? `Tel: ${this.form.envioTelefono}` : '',
      ].filter(Boolean).join('\n')
    },
    // Lo ya facturado (suma de subtotales) cubre el valor del pedido
    pedidoFacturado() {
      const facturado = this.facturas.reduce((acc, f) => acc + (Number(f.subtotal) || 0), 0)
      return this.facturas.length > 0 && this.orderValuePedido > 0 && facturado >= this.orderValuePedido - 0.009
    },
    // Ya existe un depósito para este pedido (solo se permite uno)
    tieneDeposito() {
      return this.proformas.some(p => p.tipo === 'deposito')
    },
    // % de depósito ya emitido para este pedido (prefija el campo de la proforma)
    depositoEmitidoPct() {
      return this.proformas
        .filter(p => p.tipo === 'deposito')
        .reduce((acc, p) => acc + (Number(p.proformaPct) || 0), 0)
    },
    // Total due = tramo de la proforma + envío (el depósito no lo toca)
    totalDue() {
      const pct = Number(this.form.proformaPct) || 100
      const ship = Number(this.form.shipping) || 0
      return (this.orderValue * pct) / 100 + ship
    },
    depositoAmt() {
      return (this.orderValue * (Number(this.form.depositPct) || 0)) / 100
    },
    // El depósito resta del restante, no del total due
    restante() {
      const pct = Number(this.form.proformaPct) || 100
      return Math.max(0, Math.round(((this.orderValue * (100 - pct)) / 100 - this.depositoAmt) * 100) / 100)
    },
  },
  methods: {
    // API pública: this.$refs.x.abrir(pedido)
    abrir(pedido) {
      this.pedido = pedido
      this.lineas = this.lineasDesdePedido()

      const envio = pedido.envio || {}
      this.form = {
        depositPct: 0,
        proformaPct: 100,
        shipping: 0,
        expiryDays: 7,
        restanteDias: 30,
        incoterms: 'DPD',
        paymentTerms: '70% Advance Payment',
        envioDiferente: !!envio.activo,
        envioNombre: (envio.activo && envio.nombre) || '',
        envioEmpresa: (envio.activo && envio.empresa) || '',
        envioTelefono: (envio.activo && envio.telefono) || '',
        envioDireccion: (envio.activo && envio.direccion) || '',
        envioCp: (envio.activo && envio.cp) || '',
        envioCiudad: (envio.activo && envio.ciudad) || '',
        envioPais: (envio.activo && envio.pais) || '',
        notas: '',
      }

      this.proformas = []
      this.pedidosTaller = []
      this.fabricacion = {}
      this.mostrarNueva = false
      this.mostrarDeposito = false
      this.mostrarFactura = false
      this.cargandoInfo = true
      this.show = true

      this.facturas = []
      this.cargarProformas()
      this.cargarEstadoTaller()
      this.cargarDefaultsCliente()
      this.cargarFacturas()
    },
    cargarFacturas() {
      axios
        .get(`/escaner/pedidos/${this.pedido.id}/facturas`)
        .then(({ data }) => { this.facturas = data || [] })
        .catch(() => {})
    },
    // Abre el formulario de factura con las líneas del pedido (editables);
    // el shipping se prefija con lo ya cobrado en las proformas/depósitos del pedido
    abrirFactura() {
      this.lineasFactura = this.lineasDesdePedido()
      const shipping = Math.round(this.proformas.reduce((acc, p) => acc + (Number(p.shipping) || 0), 0) * 100) / 100
      // Vencimiento por defecto: 30 días desde hoy (solo aplica si queda balance pendiente)
      const vto = new Date(Date.now() + 30 * 86400000).toLocaleDateString('sv')
      this.facturaForm = { shipping, vatTipo: 'export', notas: '', vencimiento: vto }
      this.mostrarFactura = true
      this.mostrarNueva = false
      this.mostrarDeposito = false
    },
    emitirFactura() {
      this.$bvModal
        .msgBoxConfirm(
          'Se emitirá una factura legal con numeración correlativa. Una vez emitida no se puede modificar ni borrar. ¿Continuar?',
          { title: 'Emitir factura', okTitle: 'Emitir', cancelTitle: 'Cancelar', okVariant: 'primary', centered: true },
        )
        .then(ok => {
          if (!ok) return
          this.emitiendoFactura = true
          axios
            .post(`/escaner/pedidos/${this.pedido.id}/facturas`, {
              items: this.lineasFacturaValidas.map(l => ({
                sku: l.sku,
                title: l.title,
                hsCode: l.hsCode,
                qty: parseInt(l.qty, 10) || 0,
                unitPrice: Number(l.unitPrice) || 0,
              })),
              shipping: Number(this.facturaForm.shipping) || 0,
              vatTipo: this.facturaForm.vatTipo,
              notas: this.facturaForm.notas,
              vencimiento: this.facturaForm.vencimiento || null,
            })
            .then(({ data }) => {
              this.notificar(`Factura ${data.numero} emitida`)
              this.mostrarFactura = false
              this.cargarFacturas()
              this.verPdfFactura(data.id)
            })
            .catch(e => {
              const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo emitir la factura'
              this.notificar(msg, 'danger')
            })
            .finally(() => { this.emitiendoFactura = false })
        })
    },
    // Al marcar como pagado pide la fecha del pago (por defecto hoy) y luego ejecuta cb(fecha)
    pedirFechaPago(cb) {
      this.fechaPagoFecha = new Date().toLocaleDateString('sv')
      this.fechaPagoCb = cb
      this.fechaPagoVisible = true
    },
    confirmarFechaPago() {
      if (this.fechaPagoCb) this.fechaPagoCb(this.fechaPagoFecha)
      this.fechaPagoCb = null
    },
    // Optimista, como togglePagada de proformas: cambia al momento y revierte si falla
    toggleFacturaPagada(fa) {
      if (this.pagandoFacturaId) return
      if (!fa.pagada) {
        this.pedirFechaPago(fecha => this.enviarFacturaPagada(fa, fecha))
      } else {
        this.enviarFacturaPagada(fa, null)
      }
    },
    enviarFacturaPagada(fa, fecha) {
      const actual = fa.pagada
      this.pagandoFacturaId = fa.id
      this.$set(fa, 'pagada', actual ? 0 : 1)
      axios
        .put(`/escaner/facturas/${fa.id}/pagada`, { pagada: !actual, fecha })
        .then(({ data }) => {
          this.$set(fa, 'pagada', data.pagada ? 1 : 0)
          this.$set(fa, 'fechaPago', data.pagada ? fecha : null)
          this.$set(this.pedido, 'pagado', data.pagado)
          this.notificar(data.pagada
            ? `${fa.numero}: pagada — anotado en el historial de pagos`
            : `${fa.numero}: marcada como no pagada — pago retirado`)
        })
        .catch(() => {
          this.$set(fa, 'pagada', actual)
          this.notificar('No se pudo actualizar el pago de la factura', 'danger')
        })
        .finally(() => { this.pagandoFacturaId = null })
    },
    verPdfFactura(id) {
      axios
        .get(`/escaner/facturas/${id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo abrir el PDF', 'danger') })
    },
    // Aplica las condiciones por defecto guardadas del cliente (si las tiene)
    cargarDefaultsCliente() {
      const c = this.pedido && this.pedido.cliente
      if (!c || !c.id) return
      axios
        .get(`/customers/${c.id}/proforma-defaults`)
        .then(({ data }) => {
          if (!data) return
          if (data.restanteDias != null) this.form.restanteDias = data.restanteDias
          if (data.validezDias != null) this.form.expiryDays = data.validezDias
          if (data.incoterms) this.form.incoterms = data.incoterms
          if (data.paymentTerms) this.form.paymentTerms = data.paymentTerms
        })
        .catch(() => {})
    },
    lineasDesdePedido() {
      return ((this.pedido && this.pedido.items) || []).map(it => ({
        sel: true,
        sku: it.sku || '',
        title: it.title || '',
        qty: parseInt(it.qty, 10) || 1,
        pedidoQty: parseInt(it.qty, 10) || 1,
        facturado: 0,
        unitPrice: Number(it.wspUnit) || 0,
        hsCode: '4202310010',
      }))
    },
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Proforma',
        variant,
        solidColor: true,
      })
    },
    formatFecha(f) {
      if (!f) return ''
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    fmtImporte(n) {
      const v = Math.round((Number(n) || 0) * 100) / 100
      return Number.isInteger(v) ? String(v) : v.toFixed(2)
    },
    restanteVencido(pf) {
      if (pf.pagadaRestante || !pf.restanteFecha) return false
      return new Date(pf.restanteFecha) < new Date()
    },
    resumenItems(pf) {
      const items = pf.items || []
      const uds = items.reduce((acc, it) => acc + (parseInt(it.qty, 10) || 0), 0)
      return `${items.length} producto${items.length === 1 ? '' : 's'} · ${uds} uds`
    },
    cargarProformas() {
      axios
        .get(`/escaner/pedidos/${this.pedido.id}/proformas`)
        .then(({ data }) => {
          this.proformas = data || []

          // Sincroniza el resumen de la tabla del padre (columna Proforma)
          const importe = this.proformas.reduce((acc, p) => acc + (Number(p.totalDue) || 0) + (Number(p.restante) || 0), 0)
          this.$set(this.pedido, 'proforma', this.proformas.length
            ? { n: this.proformas.length, importe: Math.round(importe * 100) / 100 }
            : null)

          // Prefija el "Depósito recibido %" con los depósitos ya emitidos
          this.form = { ...this.form, depositPct: Math.min(100, this.depositoEmitidoPct) }

          // Unidades ya incluidas en proformas, por SKU (los depósitos no consumen
          // unidades: son un anticipo sobre el pedido completo)
          const consumido = {}
          this.proformas.filter(pf => pf.tipo !== 'deposito').forEach(pf => {
            (pf.items || []).forEach(it => {
              const k = (it.sku || '').toUpperCase()
              consumido[k] = (consumido[k] || 0) + (parseInt(it.qty, 10) || 0)
            })
          })

          // La cantidad propuesta pasa a ser las unidades aún sin proforma
          this.lineas = this.lineas.map(l => {
            const udsPendientes = Math.max(0, l.pedidoQty - (consumido[l.sku.toUpperCase()] || 0))
            return {
              ...l, facturado: consumido[l.sku.toUpperCase()] || 0, qty: udsPendientes, sel: udsPendientes > 0,
            }
          })
        })
        .catch(() => {})
        .finally(() => { this.cargandoInfo = false })
    },
    // Pedidos de taller B2B creados a partir de este pedido del escáner + progreso por SKU
    cargarEstadoTaller() {
      axios
        .get('/taller/pedido/vero', { params: { tipo: 'B2B' } })
        .then(({ data }) => {
          this.pedidosTaller = (data || []).filter(p => Number(p.escanerPedidoId) === Number(this.pedido.id))
          return Promise.all(
            this.pedidosTaller.map(p => axios.get(`/taller/pedido/vero/items/${p.id}`).then(r => r.data)),
          )
        })
        .then(listas => {
          const fab = {}
          listas.flat().forEach(it => {
            const k = (it.sku || '').toUpperCase()
            if (!fab[k]) {
              fab[k] = {
                total: 0, corte: 0, mano: 0, terminado: 0,
              }
            }
            fab[k].total += Number(it.cantidadTotal) || 0
            fab[k].corte += Number(it.cantidadCorte) || 0
            fab[k].mano += Number(it.cantidadMano) || 0
            fab[k].terminado += Number(it.terminado) || 0
          })
          this.fabricacion = fab
        })
        .catch(() => {})
    },
    // Marca/desmarca uno de los dos cobros de la proforma ('due' o 'restante');
    // el pago se anota (o se retira) en el historial de pagos del pedido.
    // Optimista: el chip cambia al momento y se revierte si la API falla.
    togglePagada(pf, parte) {
      if (this.pagandoClave) return
      const campo = parte === 'restante' ? 'pagadaRestante' : 'pagada'
      if (!pf[campo]) {
        this.pedirFechaPago(fecha => this.enviarPagada(pf, parte, fecha))
      } else {
        this.enviarPagada(pf, parte, null)
      }
    },
    enviarPagada(pf, parte, fecha) {
      const clave = `${pf.id}-${parte}`
      const campo = parte === 'restante' ? 'pagadaRestante' : 'pagada'
      const actual = pf[campo]
      this.pagandoClave = clave
      this.$set(pf, campo, actual ? 0 : 1)
      axios
        .put(`/escaner/proformas/${pf.id}/pagada`, { pagada: !actual, parte, fecha })
        .then(({ data }) => {
          this.$set(pf, campo, data.pagada ? 1 : 0)
          this.$set(this.pedido, 'pagado', data.pagado)
          const etiqueta = parte === 'restante' ? 'Resto' : 'Total due'
          this.notificar(data.pagada
            ? `${pf.numero}: ${etiqueta} pagado — anotado en el historial de pagos`
            : `${pf.numero}: ${etiqueta} marcado como no pagado — pago retirado`)
        })
        .catch(() => {
          this.$set(pf, campo, actual)
          this.notificar('No se pudo actualizar el estado de pago', 'danger')
        })
        .finally(() => { this.pagandoClave = null })
    },
    // Abre el formulario de nueva proforma (limpio)
    nuevaProforma() {
      this.lineas = this.lineasDesdePedido()
      const depositPct = Math.min(100, this.depositoEmitidoPct)
      this.form = { ...this.form, depositPct, proformaPct: Math.max(0, 100 - depositPct) }
      this.mostrarNueva = true
      this.mostrarDeposito = false
      this.cargarProformas()
    },
    // Genera el documento de depósito sobre el pedido completo
    generarDeposito() {
      const items = ((this.pedido && this.pedido.items) || []).map(it => ({
        sku: it.sku,
        title: it.title,
        hsCode: '4202310010',
        qty: parseInt(it.qty, 10) || 0,
        unitPrice: Number(it.wspUnit) || 0,
      })).filter(it => it.qty > 0)

      this.generando = true
      axios
        .post(`/escaner/pedidos/${this.pedido.id}/proformas`, {
          tipo: 'deposito',
          items,
          proformaPct: Number(this.depositoPct) || 30,
          expiryDays: this.form.expiryDays,
          incoterms: this.form.incoterms,
          paymentTerms: this.form.paymentTerms,
          shippingAddress: this.shippingAddressStr,
        })
        .then(({ data }) => {
          this.notificar(`Depósito ${data.numero} generado`)
          this.mostrarDeposito = false
          this.cargarProformas()
          this.verPdf(data.id)
        })
        .catch(() => { this.notificar('No se pudo generar el depósito', 'danger') })
        .finally(() => { this.generando = false })
    },
    cerrarNueva() {
      this.mostrarNueva = false
    },
    verPdf(id) {
      axios
        .get(`/escaner/proformas/${id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => { this.notificar('No se pudo abrir el PDF', 'danger') })
    },
    generar() {
      const items = this.lineasValidas.map(l => ({
        sku: l.sku,
        title: l.title,
        hsCode: l.hsCode,
        qty: parseInt(l.qty, 10),
        unitPrice: Number(l.unitPrice) || 0,
      }))

      this.generando = true
      axios
        .post(`/escaner/pedidos/${this.pedido.id}/proformas`, { items, ...this.form, shippingAddress: this.shippingAddressStr })
        .then(({ data }) => {
          this.notificar(`Proforma ${data.numero} generada`)
          this.mostrarNueva = false
          this.cargarProformas()
          this.verPdf(data.id)
        })
        .catch(() => { this.notificar('No se pudo generar la proforma', 'danger') })
        .finally(() => { this.generando = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.pf-block {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  overflow: hidden;

  &__title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    padding: 8px 12px;
    border-bottom: 1px solid #ebe9f1;
    background: rgba(0, 0, 0, 0.015);

    &--flex {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__body {
    padding: 10px 12px;
  }

  // Contenedor único de la proforma en creación
  &--nueva {
    border-color: #d0cdf7;
  }

  &__title--main {
    font-size: 12px;
    color: #7367f0;
    background: rgba(#7367f0, 0.06);
    border-bottom: 1px solid #d0cdf7;
  }
}

// Subsecciones dentro de "Nueva proforma"
.pf-sub + .pf-sub {
  border-top: 1px solid #ebe9f1;
}

// Chip clicable de cobro: pendiente (ámbar), pagado (verde), vencido (rojo).
// El círculo funciona como checkbox: vacío = sin pagar, relleno = pagado.
.pf-pay {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 6px;
  border-radius: 8px;
  border: 1px solid rgba(255, 159, 67, 0.55);
  background: rgba(255, 159, 67, 0.06);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: all 0.15s ease;
  font: inherit;

  &__check {
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
    border-radius: 50%;
    border: 2px solid rgba(255, 159, 67, 0.7);
    background: #fff;
    color: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    transition: all 0.15s ease;
  }

  &__linea {
    display: block;
    font-weight: 700;
    color: #5e5873;
    line-height: 1.2;
  }

  &__label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #b9b9c3;
    margin-right: 5px;
  }

  &__estado {
    display: block;
    font-size: 10.5px;
    font-weight: 600;
    line-height: 1.3;
    color: #ff9f43;
  }

  &__spinner {
    width: 12px;
    height: 12px;
    border-width: 2px;
    color: currentColor;
  }

  &:disabled {
    opacity: 0.75;
    cursor: wait;
  }

  // Al pasar por encima se insinúa el check en verde (acción de marcar pagado)
  &:hover:not(.is-paid) .pf-pay__check {
    border-color: #28c76f;
    color: #28c76f;
    background: rgba(40, 199, 111, 0.08);
  }

  &.is-paid {
    border-color: rgba(40, 199, 111, 0.5);
    background: rgba(40, 199, 111, 0.08);

    .pf-pay__check {
      background: #28c76f;
      border-color: #28c76f;
      color: #fff;
    }
    .pf-pay__estado { color: #28c76f; }
  }

  &.is-overdue {
    border-color: rgba(234, 84, 85, 0.55);
    background: rgba(234, 84, 85, 0.06);

    .pf-pay__check {
      border-color: #ea5455;
      color: #ea5455;
    }
    .pf-pay__estado { color: #ea5455; }
  }
}

.dark-layout .pf-pay {
  .pf-pay__check { background: transparent; }
  .pf-pay__linea { color: #d0d2d6; }
}

// Fila de factura emitida y formulario de emisión
.pf-row--factura {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 0;
  border-bottom: 1px solid #ebe9f1;
  &:last-of-type { border-bottom: none; }
}

// Contenido del modal con overlay de generación
.pf-contenido {
  position: relative;
  min-height: 120px;
}

.pf-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(1px);
  border-radius: 6px;

  &__texto {
    font-size: 13px;
    font-weight: 600;
    color: #5e5873;
  }
}

.dark-layout .pf-overlay {
  background: rgba(40, 48, 70, 0.85);

  .pf-overlay__texto { color: #d0d2d6; }
}

// Zona de acciones (crear depósito / nueva proforma)
.pf-acciones {
  display: flex;
  justify-content: center;
  gap: 14px;
  padding: 10px 0 6px;
}

.pf-accion {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 220px;
  padding: 16px 18px;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.18);
    transform: translateY(-1px);
  }

  &__icono {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    background: rgba(#7367f0, 0.1);
    color: #7367f0;
    margin-bottom: 2px;
  }

  &__titulo {
    font-size: 13px;
    font-weight: 700;
    color: #5e5873;
  }

  &__desc {
    font-size: 11px;
    color: #6e6b7b;
    text-align: center;
  }

  &--primary {
    border-color: rgba(#7367f0, 0.45);
    background: rgba(#7367f0, 0.04);
  }
}

.dark-layout {
  .pf-accion {
    background: #283046;
    border-color: #3b4253;

    &__titulo { color: #d0d2d6; }

    &--primary {
      border-color: rgba(#7367f0, 0.6);
      background: rgba(#7367f0, 0.08);
    }
  }
}

.pf-cobro {
  min-width: 0;
}

.pf-cancelar {
  border: none;
  background: transparent;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  cursor: pointer;
  padding: 0;

  &:hover { color: #ea5455; }
}

.pf-group {
  border-bottom: 1px solid #ebe9f1;

  &:last-child { border-bottom: none; }
}

.pf-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
}

.pf-detalle {
  padding: 4px 12px 8px 48px;
  background: rgba(0, 0, 0, 0.015);

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 4px 0;

    > .min-w-0 { flex: 1; }
  }

  &__title {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
  }
}

.pf-items {
  max-height: 300px;
  overflow-y: auto;
}

.pf-item {
  display: grid;
  grid-template-columns: 28px 1fr 210px 65px 80px;
  gap: 8px;
  align-items: center;
  padding: 7px 12px;
  border-bottom: 1px solid #ebe9f1;

  &:last-child { border-bottom: none; }

  &--head {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    padding: 6px 12px;
  }

  &--off {
    opacity: 0.45;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
  }
}

.min-w-0 { min-width: 0; }

.pf-fab {
  color: #6e6b7b;
  white-space: nowrap;
}

.pf-input {
  text-align: right;
}

.pf-check {
  margin: 0;
}

.pf-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  margin-bottom: 2px;
  display: block;
}

.pf-footer {
  display: flex;
  align-items: center;
  gap: 28px;
}

@media (max-width: 767px) {
  .pf-item {
    grid-template-columns: 28px 1fr 60px 75px;

    > :nth-child(3) { display: none; }
  }
}
</style>
